import {Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';
import { DOCUMENT } from '@angular/common';

import { StepComponent } from '../../step.component';
import { PlanStep } from '../../../services/PlanStep';
import { PlanCoordinate } from '../../../services/plan-coordinate';
import { PlanStepPage } from '../../../services/PlanStepPage';
import { ResourceService } from '../../../services/resource.service';
import { IResource } from '../../../services/IResource';
import { ValidationService } from '../../../../auth/services/validation.service';
import { ValidationErrors } from '@angular/forms/src/directives/validators';
import { Observable } from 'rxjs/Observable';
import { timer } from 'rxjs/observable/timer';
import { of } from 'rxjs/observable/of';
import { catchError, distinctUntilChanged, filter, map, pluck, switchMap } from 'rxjs/operators';
import {AttachmentUploadModalComponent} from '../../../../shared/components/attachment-upload-modal/attachment-upload-modal.component';
import {Subscription} from 'rxjs/index';
import {StrategyService} from '../../../services/strategy.service';
import {StrategiesDataStoreService} from '../../../../shared/services/strategies-data-store.service';
import {take} from 'rxjs/internal/operators';
import {AppLoadingService} from '../../../../app-loading-panel/app-loading.service';

@Component({
  selector: 'app-judge-success',
  templateUrl: './judge-success.component.html',
  styleUrls: ['./judge-success.component.scss']
})
export class JudgeSuccessComponent extends StepComponent<IResource> implements OnInit, OnDestroy {

  @ViewChild('attachmentUploadModal') private uploadModal: AttachmentUploadModalComponent;

  @Input() strategyId: number; // TODO remove strategy id dependency

  @Output() planCoordinateChange = new EventEmitter<PlanCoordinate>();
  // @Output() updateImpact = new EventEmitter<string>();
  @Output() resourceCreate = new EventEmitter<IResource>();
  @Output() resourceEdit = new EventEmitter<IResource>();
  @Output() resourcesChange = new EventEmitter<void>();

  readonly MAX_IMPACT_LENGTH = 2000;
  private readonly EXTENSION_RE = /\.[0-9a-z]+$/i;
  private readonly HTTP_PREFIX_RE = /^https?:\/\//i;
  private readonly _evidenceType = 'S';

  private pristineResource: IResource;
  private _externalLinkForm: FormGroup;
  private _attachFilesForm: FormGroup;
  impact: string;
  // private resourcesSubscription: Subscription;
  private _subscriptions: Subscription[] = [];

  resources: IResource[] = [];

  constructor(@Inject(DOCUMENT) private document: Document,
              private formBuilder: FormBuilder,
              private validationService: ValidationService,
              _resourceService: ResourceService,
              private _loadingService: AppLoadingService,
              private _strategyService: StrategyService,
              private _strtDataStore: StrategiesDataStoreService) {
    super(PlanCoordinate.stepBy(PlanStep.DETERMINE_IMPACT_OF_CRITICAL_INITIATIVES), _resourceService);
  }


  ngOnInit() {
    this._subscriptions.push(this._resourceService.resources$.subscribe(this.resourceHandler.bind(this)));
    this._subscriptions.push(this._strtDataStore.impactStatement$.subscribe(this.impactHandler.bind(this)));
    this._resourceService.getResources(this.strategyId);
    this._strategyService.getImpact(this.strategyId);
  }

  resourceHandler(newResources: IResource[]) {
    this.resources = newResources.filter((resource: IResource) => {
      return resource.evidenceType.toUpperCase() === 'S';
    });
    this._loadingService.clearLoading();
  }

  refetchResources() {
    this._loadingService.setLoading();
    this._resourceService.getResources(this.strategyId);
  }

  impactHandler(impact: string) {
    this.impact = impact;
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach((subscription: Subscription) => {
      subscription.unsubscribe();
    });
  }

  get resourcesColWidth() {
    return !this.isEditItemDisabled && !this.isDeleteItemDisabled ? '3' : '2';
  }

  onClickViewTheoryOfAction() {
    this.planCoordinateChange.emit(PlanCoordinate.stepPageBy(PlanStepPage.TheoryOfAction));
  }

  iconImageSrc(resource: IResource) {
    return `assets/icons/${resource.fileGroupIcon}`;
  }

  altText(resource: IResource) {
    return resource.isUrl ? 'External Link' : 'Attached File';
  }

  onClickUploadFiles() {
    this.showAttachmentUploadModal(this._evidenceType);
  }

  onClickResource(resource: IResource) {
    if (resource.isUrl) {
      if (!resource.resourcePath.match(this.HTTP_PREFIX_RE)) {
        // if the URL is not prefixed with HTTP, then add it.
        resource.resourcePath = `http://${resource.resourcePath}`;
      }
      window.open(resource.resourcePath);
    } else {
      const iframe = this.document.createElement('iframe');
      iframe.src = this._resourceService.transformToResourceDownloadUrl(resource);
      this.document.body.appendChild(iframe);
      setTimeout(() => iframe.remove(), 1000);
    }
  }

  onClickEditAttachment(resource: IResource) {
    this.uploadModal.edit(this._evidenceType, resource);
  }


  editResource(resource: IResource) {
    this._resourceService.editResource(resource).subscribe((res) => {
        this._resourceService.getResources(this.strategyId);
      }, (err) => {
        console.log('Edit Err: ', err);
        this.uploadModal.close();
      }
    );
  }

  createResource(resource: IResource) {
    // this.resourceCreate.emit(resource);
    if (resource.isUrl) {
      // create external link resource
      this._resourceService.createLinkResource(this.strategyId, resource)
        .subscribe(() =>  {
          // this.resourceService.getResources(this.strategyId)
          //   this.fetchData();
          this.refetchResources();

        }, (err) => {
          console.error(`Caught error in creating resource: ${JSON.stringify(err)}`);
        });
    } else {
      console.warn('onResourceCreate', resource);
    }
  }



  // =========================================================================


  get externalLink() {
    return this.externalLinkForm.get('externalLink');
  }

  get externalLinkName() {
    return this.externalLinkForm.get('externalLinkName');
  }

  get externalLinkDescription() {
    return this.externalLinkForm.get('externalLinkDescription');
  }

  get externalLinkForm(): FormGroup {
    if (!this._externalLinkForm) {
      this._externalLinkForm = this.createExternalLinkForm();
    }
    return this._externalLinkForm;
  }

  private createExternalLinkForm() {
    return this.formBuilder.group({
      externalLink: ['', [control => this.validateExternalLink(control), this.validationService.resourceLinkValidator]],
      externalLinkName: ['', control => this.validateAttachmentName(control), control => this.validateDuplicateAttachmentName(control)],
      externalLinkDescription: ['', control => this.validateAttachmentDescription(control)]
    });
  }


  get attachmentFileName() {
    return this.attachFilesForm.get('attachmentFileName');
  }


  get attachmentFileDescription() {
    return this.attachFilesForm.get('attachmentFileDescription');
  }

  get attachFilesForm(): FormGroup {
    if (!this._attachFilesForm) {
      this._attachFilesForm = this.createAttachFilesForm();
    }
    return this._attachFilesForm;
  }

  private createAttachFilesForm() {
    return this.formBuilder.group({
      attachmentFile: [[], (it) => this.attachmentFileValidate(it)],
      attachmentFileName: ['', control => this.validateAttachmentName(control), control => this.validateDuplicateAttachmentName(control)],
      attachmentFileDescription:  ['', control => this.validateAttachmentDescription(control)]
    });
  }


  private attachmentFileValidate(control: AbstractControl): ValidationErrors | null {
    let errors = null;
    const files: File[] = control.value;
    if (this.pristineResource) {
      if (files.length) {
        errors = {attachmentFileNotEditable: true, error: true};
      }
    } else if (!files.length) {
      errors = {attachmentFileRequired: true, error: true};
    } else if (files[0].size > ResourceService.MAX_FILE_SIZE) {
      errors = {attachmentFileSize: true, error: true};
    } else if (!this.isValidExtension(files[0])) {
      errors = {attachmentFileType: true, error: true};
    }
    return errors;
  }

  isValidExtension(file: File) {
    const ext = file.name.match(this.EXTENSION_RE)[0].toLowerCase();
    return ResourceService.ACCEPT_EXTENSIONS.includes(ext);
  }

  private validateExternalLink(control: AbstractControl) {
    if (!control.value.trim().length) {
      return {externalLinkUrlRequired: true, error: true};
    } else {
      return null;
    }
  }

  private validateAttachmentDescription(control: AbstractControl) {
    if (!control.value.trim().length) {
      return {attachmentDescriptionRequired: true, error: true};
    } else {
      return null;
    }
  }

  private validateAttachmentName(control: AbstractControl) {
    if (!control.value.trim().length) {
      return {attachmentFileNameRequired: true, error: true};
    } else {
      return null;
    }
  }

  private validateDuplicateAttachmentName(control: AbstractControl): Observable<{}> {
    return timer(750).pipe(// wait 3/4 of a second
      map(() => control.value.trim()), // get the current name
      filter(resourceName => !!resourceName), // throw away empty strings
      filter(resourceName => true),
      distinctUntilChanged(), // make sure it changed
      switchMap(resourceName => {
        // in edit mode throw away matches to original name
        const isChanged = !(this.pristineResource && (this.pristineResource.name === resourceName));
        if (isChanged) {
          return this._resourceService.validateResourceName(this.strategyId, resourceName);
        } else {
          return of({isDuplicateResourceName: false});
        }
      }),
      pluck('isDuplicateResourceName'), // pluck out the value of the isDuplicateResourceName property
      map(dupe => {
        if (dupe) {
          return {duplicateResourceLinkName: true, error: true};
        } else {
          return null;
        }
      }),
      catchError((err: any, caught: Observable<null>) => {
        console.error(err); // TODO integrate error messages
        return null;
      })
    );
  }

  /**
   * Will present the attachment modal with a given location
   * @param categoricalLocation
   */
  showAttachmentUploadModal(categoricalLocation) {
    this.uploadModal.show(categoricalLocation);
  }


  /**
   * Will delete the given resource
   * @param {IResource} resource
   */
  deleteResource(resource: IResource) {
    this._resourceService.deleteResource(resource.resourceId).subscribe(() => {
      this._resourceService.getResources(this.strategyId);
    }, (err) => {
      console.error(`Caught error on delete resource: ${JSON.stringify(err)}`);
    });
  }

  updateImpact(impactStatement: string) {
    this._strategyService.updateImpact(this.strategyId, impactStatement)
      .pipe(take(1)).subscribe((blankResposne: {}) => {
      // this._strtDataStore.impactStatement = impactStatement;
    }, (err) => {
      console.error(`Error updating impact statement: `, err);
    });
  }

}
